import React, { useEffect, useState } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Billing() {
  const { workspace, refreshWorkspace } = useAuth();
  const [plans, setPlans] = useState([]);
  const [periods, setPeriods] = useState([{ months: 1, label: "Bulanan", discount_pct: 0 }]);
  const [selectedMonths, setSelectedMonths] = useState(1);
  const [subs, setSubs] = useState([]);
  // Provider dipilih otomatis di sisi server berdasarkan konfigurasi Super Admin.
  const [providerInfo, setProviderInfo] = useState(null);

  const load = async () => {
    const pub = await api.get("/admin/public-plans").catch(() => ({ data: { plans: [], billing_periods: [] } }));
    const paid = (pub.data.plans || []).filter((p) => p.price > 0);
    setPlans(paid);
    const bps = pub.data.billing_periods?.length ? pub.data.billing_periods : [{ months: 1, label: "Bulanan", discount_pct: 0 }];
    setPeriods(bps);
    if (!bps.find((b) => b.months === selectedMonths)) setSelectedMonths(bps[0].months);
    api.get("/admin/payment-provider").then((r) => setProviderInfo(r.data)).catch(() => {});
    const [xsubs, dsubs] = await Promise.all([
      api.get("/xendit/subscriptions").catch(() => ({ data: [] })),
      api.get("/doit/subscriptions").catch(() => ({ data: [] })),
    ]);
    setSubs([...(xsubs.data || []).map((s) => ({ ...s, provider: s.provider || "xendit" })), ...(dsubs.data || [])]
      .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
  };
  useEffect(() => { load(); }, []);

  const calcAmount = (price) => {
    const bp = periods.find((b) => b.months === selectedMonths) || periods[0];
    const gross = price * (bp?.months || 1);
    return Math.round(gross * (1 - (bp?.discount_pct || 0) / 100));
  };
  const activePeriod = periods.find((b) => b.months === selectedMonths) || periods[0];

  const subscribe = async (planId) => {
    try {
      const provider = providerInfo?.active_provider === "doit" ? "doit" : "xendit";
      const r = await api.post(`/${provider}/subscribe`, { plan_id: planId, months: selectedMonths });
      toast.success(`Tagihan ${activePeriod?.label || selectedMonths + " bulan"} dibuat`);
      const url = r.data.invoice_url || r.data.hosted_url;
      if (url && !url.includes("simulated")) window.open(url, "_blank");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal buat langganan"); }
  };

  const simulatePay = async (s) => {
    const path = s.provider === "doit" ? `/doit/simulate-pay/${s.id}` : `/xendit/simulate-pay/${s.id}`;
    await api.post(path);
    toast.success("Simulasi pembayaran sukses. Plan Anda diupgrade!");
    await refreshWorkspace();
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl" data-testid="billing-page">
      <div>
        <h1 className="font-serif-display text-4xl font-medium text-slate-900">Billing &amp; Berlangganan</h1>
        <p className="text-sm text-slate-500 mt-1">Pilih paket yang sesuai kebutuhan Anda. Pembayaran aman melalui QRIS, VA, e-wallet, dan kartu kredit.</p>
      </div>

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900" data-testid="current-plan-banner">
        <div>Plan aktif: <b className="uppercase">{workspace?.plan || "free"}</b></div>
        {providerInfo && (
          <div className="text-xs mt-1 text-amber-700">
            Gateway pembayaran: <b className="uppercase">{providerInfo.active_provider === "none" ? "Simulasi" : providerInfo.active_provider}</b>
            {providerInfo.active_provider !== "none" && (
              <> ({providerInfo.active_provider === "xendit" ? (providerInfo.xendit?.configured ? providerInfo.xendit.mode : "belum dikonfigurasi") : (providerInfo.doit?.configured ? providerInfo.doit.mode : "belum dikonfigurasi")})</>
            )}
          </div>
        )}
      </div>

      <h2 className="font-serif-display text-2xl font-medium text-slate-900 mt-8 mb-4">Pilih Paket</h2>
      {periods.length > 1 && (
        <div className="inline-flex rounded-lg border border-slate-300 bg-slate-50 p-1 mb-5" data-testid="billing-period-switcher">
          {periods.map((bp) => (
            <button key={bp.months}
              onClick={() => setSelectedMonths(bp.months)}
              className={`relative px-4 py-1.5 text-sm rounded-md transition-colors ${selectedMonths === bp.months ? "bg-slate-900 text-white shadow" : "text-slate-700 hover:bg-white"}`}
              data-testid={`period-${bp.months}m-btn`}>
              {bp.label}
              {bp.discount_pct > 0 && <span className="ml-1.5 text-[10px] px-1 rounded bg-emerald-500/90 text-white tabular-nums">-{bp.discount_pct}%</span>}
            </button>
          ))}
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        {plans.map((p) => {
          const amount = calcAmount(p.price);
          const perMonth = Math.round(amount / (activePeriod?.months || 1));
          return (
          <div key={p.id} className={`bg-white border rounded-md p-6 ${workspace?.plan === p.id ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-200"}`} data-testid={`plan-card-${p.id}`}>
            <div className="text-xs uppercase tracking-wider text-slate-500">{p.name}</div>
            {activePeriod?.months > 1 && activePeriod?.discount_pct > 0 && (
              <div className="text-xs text-slate-400 line-through mt-1">{formatIDR(p.price * activePeriod.months)}</div>
            )}
            <div className="font-serif-display text-3xl font-semibold text-slate-900 mt-1 tabular-nums" data-testid={`plan-amount-${p.id}`}>{formatIDR(amount)}</div>
            <div className="text-xs text-slate-500">
              /{activePeriod?.label?.toLowerCase() || "bulan"}
              {activePeriod?.months > 1 && <span className="ml-1">· setara {formatIDR(perMonth)}/bulan</span>}
            </div>
            <Button
              className={`w-full mt-4 ${workspace?.plan === p.id ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-900 hover:bg-slate-800"} text-white`}
              onClick={() => subscribe(p.id)}
              disabled={workspace?.plan === p.id}
              data-testid={`subscribe-${p.id}-btn`}
            >
              {workspace?.plan === p.id ? "Plan Aktif" : "Bayar Sekarang"}
            </Button>
            <div className="mt-3 text-[11px] uppercase tracking-widest text-slate-400">{p.feature_count} modul aktif</div>
            <ul className="mt-2 space-y-1.5 text-xs">
              {(p.features_enabled || []).slice(0, 6).map((f, i) => <li key={i} className="flex items-start gap-1.5"><CheckCircle size={12} weight="fill" className="text-emerald-600 mt-0.5" /><span className="text-slate-700">{f}</span></li>)}
              {(p.features_enabled || []).length > 6 && <li className="text-slate-400 italic pl-4">+ {p.features_enabled.length - 6} modul lainnya</li>}
            </ul>
          </div>
          );
        })}
        {plans.length === 0 && <div className="col-span-3 text-center text-slate-400 py-10">Memuat paket…</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 mt-8">
        <h3 className="font-serif-display text-2xl font-medium">Riwayat Transaksi</h3>
        <div className="mt-4 space-y-2" data-testid="subscriptions-list">
          {subs.length === 0 && <div className="text-slate-500 text-sm">Belum ada transaksi.</div>}
          {subs.map((s) => (
            <div key={s.id} className="border border-slate-200 rounded p-3" data-testid={`sub-${s.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-sm font-medium capitalize flex items-center gap-2">
                    {s.plan_id}
                    {s.rail && <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700">{s.rail}</span>}
                  </div>
                  <div className="text-xs text-slate-500 tabular-nums">{formatIDR(s.amount)} · {formatDate(s.created_at)}</div>
                  {s.reference && <div className="text-[10px] font-mono-plex text-slate-400 mt-0.5">{s.reference}</div>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${s.status === "PAID" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{s.status}</span>
              </div>
              {s.status !== "PAID" && (s.status || "").includes("SIMULATION") && (
                <Button size="sm" variant="outline" className="mt-2 w-full" onClick={() => simulatePay(s)} data-testid={`sim-pay-${s.id}`}>Simulasi Pembayaran Berhasil</Button>
              )}
              {(s.hosted_url || s.invoice_url) && !(s.hosted_url || s.invoice_url || "").includes("simulated") && (
                <a href={s.hosted_url || s.invoice_url} target="_blank" rel="noreferrer" className="text-xs text-amber-700 underline mt-2 block">Buka halaman pembayaran</a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
